import { Flex } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { CreateOptimizationRequest } from 'src/api/mutations';
import {
  ApiModelVersionDataPoint,
  OptimizationConstraint,
  useInvestmentsQuery,
} from 'src/api/queries';
import { FormNumberInput } from 'src/components/molecules/formInput/FormNumberInput';
import { FormTextInput } from 'src/components/molecules/formInput/FormTextInput';
import { FormTimePeriodInput } from 'src/components/molecules/formInput/FormTimePeriodInput';
import { useMODataContext } from 'src/features/MO/contexts/MODataContext';
import { dayjs } from 'src/libs/dayjs';
import { Constraints } from '../Constraints';
import { Footer } from './Footer';
import { ErrorNotifBox } from 'src/components/atoms/ErrorNotifBox';
import { toApiDates } from 'src/utils/date';

export type ConstraintType = 'FIXED' | 'MAX' | 'MIN';

export type Constraint = {
  type: ConstraintType;
  value: number | null;
  media: string;
  period: {
    from: Date;
    to: Date;
    entirePeriod: boolean;
  };
};

export type OptimizationFormInputs = {
  name: string;
  investment: number;
  periodStart: Date;
  periodEnd: Date;
  constraints: Array<Constraint>;
};

type OptimizationContentProps = {
  onClose: () => void;
  currentModel: ({ customerName: string } & ApiModelVersionDataPoint) | null;
};

export const OptimizationContent: React.FC<OptimizationContentProps> = ({
  onClose,
  currentModel,
}) => {
  const { createOptimization, investments } = useMODataContext();

  if (!currentModel) {
    return <ErrorNotifBox title="No model version selected" />;
  }

  const initPeriodData = {
    periodStart: dayjs(currentModel.dataStartDate).toDate(),
    periodEnd: dayjs(currentModel.dataEndDate).toDate(),
  };

  const methods = useForm<OptimizationFormInputs>({
    defaultValues: {
      name: '',
      investment: Math.round(investments.totalInvestment),
      constraints: [],
      ...initPeriodData,
    },
  });

  // The optimizer starts from the actual spend in the selected period, so the budget
  // defaults to that spend and follows the period; the user can still change it.
  const periodStart = methods.watch('periodStart');
  const periodEnd = methods.watch('periodEnd');
  const { data: periodInvestments } = useInvestmentsQuery(
    {
      customer: currentModel.customerName,
      model_build: currentModel.buildID,
      ...toApiDates({ start: dayjs(periodStart), end: dayjs(periodEnd) }),
    },
    !!periodStart && !!periodEnd,
  );
  const periodSpend = useMemo(
    () =>
      periodInvestments?.reduce(
        (total, datapoint) =>
          total +
          datapoint.investments.reduce((sum, { value }) => sum + value, 0),
        0,
      ),
    [periodInvestments],
  );
  useEffect(() => {
    if (periodSpend !== undefined) {
      methods.setValue('investment', Math.round(periodSpend));
    }
  }, [periodSpend, methods]);

  const onSubmit = async (values: OptimizationFormInputs) => {
    const optimizationPeriod = {
      from: dayjs(values.periodStart).format('YYYY-MM-DD'),
      to: dayjs(values.periodEnd).format('YYYY-MM-DD'),
    };

    const constraints: Array<OptimizationConstraint> = values.constraints
      .map((c) => {
        const isEntirePeriod = c.period.entirePeriod;
        const period = {
          from: dayjs(c.period.from).format('YYYY-MM-DD'),
          to: dayjs(c.period.to).format('YYYY-MM-DD'),
        };
        return {
          ...c,
          period: isEntirePeriod ? optimizationPeriod : period,
        };
      })
      .filter((c): c is OptimizationConstraint => {
        return c.value !== null;
      });

    const createOptimizationBody: CreateOptimizationRequest = {
      customer: currentModel.customerName,
      optimizationName: values.name,
      targetInvestment: values.investment,
      constraints,
      period: optimizationPeriod,
      modelBuild: currentModel.buildID,
      modelId: currentModel.modelID,
    };
    try {
      await createOptimization(createOptimizationBody);
      onClose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      window.alert('Creation of optimization failed');
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Flex flexDir={'column'}>
          <FormTextInput<OptimizationFormInputs>
            inputPath="name"
            placeholder="Enter name"
            helperText="Used to help identify your optimization"
            label="Optimization name"
            required
            inputProps={{
              background: 'unset',
              border: '1px solid',
              borderColor: 'border.box !important',
              _placeholder: {
                color: 'text.darkGray !important',
              },
            }}
            sx={{ mb: { base: '12px', md: '24px' } }}
          />
          <FormTimePeriodInput<OptimizationFormInputs>
            periodFromInputPath="periodStart"
            periodToInputPath="periodEnd"
            helperText={`Selected date range for the optimization`}
            label="Date"
            sx={{ mb: { base: '12px', md: '24px' } }}
          />
          <FormNumberInput<OptimizationFormInputs>
            helperText={
              periodSpend !== undefined
                ? `Total target investment for time period. Actual spend in the period: ${Math.round(
                    periodSpend,
                  ).toLocaleString('fi-FI')} €`
                : `Total target investment for time period`
            }
            inputPath="investment"
            required
            suffix="€"
            label="Investment"
            placeholder="e.g. €20,000"
            sx={{ mb: { base: '12px', md: '24px' } }}
          />
        </Flex>
        <Constraints />
        <Footer onCancelClick={onClose} />
      </form>
    </FormProvider>
  );
};
